/* eslint-disable max-len */
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';


import * as uuid from 'uuid';
import moment from 'moment-timezone';
import { BehaviorSubject, Observable, Subscription, filter, mergeMap, of, tap } from 'rxjs';
import { TimeUnit } from '@amcharts/amcharts4/core';
import { cloneDeep } from 'lodash';

import { BucketFunctionApiEnum } from '@api/mentions/enum/bucket-function-api.enum';
import { NeoFacetTermFieldApiEnum } from '@api/mentions/enum/neo-facet-term-field-api.enum';
import { NeoFacetDateFieldApiEnum } from '@api/mentions/model/neo-facet-date-field-api.enum';
import { MentionsApi } from '@api/mentions/mentions.api';
import { SavedMentionSearchesApi } from '@api/mentions/saved-mention-searches.api';
import { DateQueryTypeEnum } from '@api/mentions/enum/date-query-type.enum';
import { DateFacetValueModel } from '@api/mentions/model/date-facet-value.model';
import { BucketResponseModel } from '@api/mentions/model/bucket-response.model';
import { SearchCriteriaQuerySentimentsEnum } from '@api/mentions/enum/search-criteria-query-sentiments.enum';
import { MentionsSearchResponseModel } from '@api/mentions/model/mentions-search-response.model';
import { SearchCriteriaDateQueryModel } from '@api/mentions/model/search-criteria-date-query.model';

import { PositiveNegativeRiverChartModel } from '@Common/Charts/models/PositiveNegativeRiverChart/positive-negative-river-chart.model';
import { DateAndValueXYChart } from '@Common/Charts/models/date-and-value-x-y.chart';
import { DateAndValueXYChartOptionsModel } from '@Common/Charts/models/date-and-value-x-y-chart-options.model';
import { DataPointModel } from '@Common/Charts/models/data-point.model';
import { chartColors } from '@Common/Charts/enums/chart-colors.enum';
import { LineSeriesModel } from '@Common/Charts/models/lineChart/line-series.model';
import { ChartStylesModel } from '@Common/Charts/models/chart-styles.model';
import { StackedRiverChartModel } from '@Common/Charts/models/StackedRiverChart/stacked-river-chart.model';
import { ReportsNeoFacetKeys } from '@Common/Reports/enums/reports-neo-facet-keys.enum';
import { DateQueryModel } from '@Common/Forms/models/date-query.model';
import { ChartMetrics, ChartMetricsEnum } from '@Common/Reports/enums/chart-metrics.enum';
import { MultiSeriesDateAndValueXYChart } from '@Common/Charts/models/multi-series-date-and-value-x-y.chart';
import { ChartMetadataModel } from '@Common/Charts/models/chart-metadata.model';

import { userAppPreferencesDefault } from '@Core/Users/data/user-app-preferences-default';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { IndexeddbCoreCacheService } from '@Core/Cache/services/indexeddb-core-cache.service';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { PreferencesModel } from '@Core/Users/models/preferences.model';

import { MonitoringRoutingService } from '@Monitor/Core/services/monitoring-routing.service';
import { MentionSearchCriteriaModel } from '@Monitor/Shared/models/mention-search-criteria.model';
import { MonitoringBackEndDataSourceModel } from '@Monitor/Shared/models/monitoring-back-end-data-source.model';

import { AlertsService } from './alerts.service';
import { AlertModel } from '../models/alert.model';
import { AlertSurgeMetricEnum } from '../enums/alert-surge-metric.enum';
import { AlertHistoryModel } from '../models/alert-history.model';
import { ReportBlockTypeEnum } from '@Common/Reports/enums/report-block-type.enum';
import { ReportSeriesModel } from '@Common/Reports/models/report-series.model';
import { ReportDateRangeModel } from '@Common/Reports/models/report-date-range.model';
import { ReportSeriesMentionsModel } from '@Common/Reports/models/report-series-mentions.model';
import { SeriesTypeEnum } from '@Common/Reports/enums/series-type.enum';
import { DateQueryDateTypeEnum } from '@api/mentions/enum/date-query-date-type.enum';
import { ChartDisplaySettingsModel } from '@Common/Charts/models/chart-display-settings.model';
import { SentimentModel } from '@Monitor/Shared/models/sentiment.model';
import { MediaSubscriptionApiService } from '@api/mediaSubscriptions/media-subscription.api.service';
import { ThemeApiService } from '@api/social/theme.api.service';
import { MentionSearchQueryModel } from '@Monitor/Shared/models/mention-search-query.model';
import { DateFacetTypesEnum } from '@api/mentions/enum/date-facet-types.enum';


@Injectable()
export class AlertsSurgeChartService implements OnDestroy {
    alert: AlertModel;
    routeId: number;

    hideSlideOverlay: boolean = false;

    searchCriteria: MentionSearchCriteriaModel;
    filterCriteria: MentionSearchCriteriaModel;

    chart: PositiveNegativeRiverChartModel | DateAndValueXYChart | MultiSeriesDateAndValueXYChart;
    private chart$: BehaviorSubject<PositiveNegativeRiverChartModel | DateAndValueXYChart> =
    // eslint-disable-next-line @stylistic/indent
        new BehaviorSubject<PositiveNegativeRiverChartModel | DateAndValueXYChart>(null);
    chartOptions: DateAndValueXYChartOptionsModel;

    preferences: PreferencesModel;
    socialTopicThemeIds: number[] = [];

    sentimentChart: StackedRiverChartModel;
    private sentimentChart$: BehaviorSubject<StackedRiverChartModel> = new BehaviorSubject<StackedRiverChartModel>(null);
    sentimentChartOptions: DateAndValueXYChartOptionsModel;

    metaData = {
        noContentLabel: this.localizationService.transform('Monitoring.result.noContentFound')
    } as ChartMetadataModel;

    readonly defaultChartColors = userAppPreferencesDefault.reportsPrefs.defaultChartColors;

    requests: Subscription;

    maxYValue: number = 0;
    minYValue: number = 0;
    avgYValue: number = 0;
    avgNegYValue: number = 0;
    neoFacetKey: string = 'OVER_TIME';

    alertHistory: AlertHistoryModel;
    protected userTimezonePreference: string;

    subscriptions: Subscription[] = [];

    constructor(
        private alertsService: AlertsService,
        private monitoringRoutingService: MonitoringRoutingService,
        public localizationService: LocalizationService,
        public mentionsApi: MentionsApi,
        private savedSearchesApi: SavedMentionSearchesApi,
        public indexedDbCoreCacheService: IndexeddbCoreCacheService,
        private currentUserStateService: CurrentUserStateService,
        private mediaSubscriptionApiService: MediaSubscriptionApiService,
        private themeApiService: ThemeApiService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
    ) {
        this.subscriptions.push(
            this.alertsService.getCurrentAlert().subscribe(alert => {
                if (alert) {
                    this.alert = alert;
                }
            }),
            this.monitoringRoutingService.getCurrentRoute().subscribe(route => {
                this.routeId = +route?.value;
            }),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(
                preferences => {
                    this.preferences = preferences;
                    this.userTimezonePreference = preferences.timeZone;
                }
            )
        );
    }

    getSearches(impactfulDataChangeSinceSent: boolean = false): void {
        this.getSearchSource(impactfulDataChangeSinceSent)?.pipe(
            mergeMap(() => this.savedSearchesApi.getById(this.alert.filterSearch.id))
        ).subscribe({
            next: (savedMentionSearch) => {
                this.filterCriteria = new MentionSearchCriteriaModel(savedMentionSearch);
                this.generateChart();
            },
            error: (err) => {
                // if getSavedSearch fails, set blank charts
                this.chart = new MultiSeriesDateAndValueXYChart([]);
                this.sentimentChart = new StackedRiverChartModel([]);
                this.setChart();
                this.getSentimentChart();
            }
        });
    }

    private getSearchSource(impactfulDataChangeSinceSent: boolean): Observable<any> {
        let getSearchModelRequest: Observable<any> = of(null);
        this.metaData.primarySeriesLabelText = null;
        this.metaData.primarySeriesUrl = null;
        this.alertsService.setAlertSourceChanged(false);
        if (this.alert.alertIsAgentSearch || this.alert.alertIsSubscriptionSearch) {
            this.searchCriteria = null;
            this.socialTopicThemeIds = [];
            getSearchModelRequest = this.mediaSubscriptionApiService.getById(this.alert.searchCriteriaId).pipe(
                tap(mediaSubscriptionApiModel => {
                    this.socialTopicThemeIds = mediaSubscriptionApiModel.themeIds;
                    if (impactfulDataChangeSinceSent) {
                        this.metaData.primarySeriesLabelText = null;
                    } else {
                        this.metaData.primarySeriesLabelText = mediaSubscriptionApiModel.label;
                        if (this.alert.alertIsAgentSearch && this.socialTopicThemeIds?.length) {
                            this.metaData.primarySeriesUrl = this.appBaseHref + `Social/themes/theme/${this.socialTopicThemeIds[0]}/postFeed`;
                        } else {
                            this.metaData.primarySeriesUrl = this.appBaseHref + `Monitoring/subscriptions/subscription/${this.alert.searchCriteriaId}`;
                        }
                    }
                })
            );
        } else if (this.alert.alertIsThemeSearch) {
            this.searchCriteria = null;
            if (impactfulDataChangeSinceSent) {
                this.metaData.primarySeriesLabelText = null;
            } else {
                getSearchModelRequest = this.themeApiService.getById(this.alert.searchCriteriaId).pipe(
                    tap(themeApiModel => {
                        this.metaData.primarySeriesLabelText = themeApiModel.name;
                        this.metaData.primarySeriesUrl = this.appBaseHref + `Social/themes/theme/${this.alert.searchCriteriaId}/postFeed`;
                        // FE check to see if the theme has changed since the submittedDate
                        if (themeApiModel.modifiedDate && (moment(themeApiModel.modifiedDate) > moment(this.alertHistory?.submittedDate))) {
                            this.alertsService.setAlertSourceChanged(true);
                        }
                    })
                );
            }
        } else {
            getSearchModelRequest = this.savedSearchesApi.getById(this.alert.searchCriteriaId).pipe(
                tap(topic => {
                    this.searchCriteria = new MentionSearchCriteriaModel(topic);
                    if (impactfulDataChangeSinceSent) {
                        this.metaData.primarySeriesLabelText = null;
                    } else {
                        this.metaData.primarySeriesLabelText = topic.name;
                        this.metaData.primarySeriesUrl = this.appBaseHref + `Monitoring/topics/topic/${this.alert.searchCriteriaId}`;
                    }
                    // FE check to see if the topic has changed since the submittedDate
                    if (topic.lastModifiedDate && (moment(topic.lastModifiedDate) > moment(this.alertHistory?.submittedDate))) {
                        this.alertsService.setAlertSourceChanged(true);
                    }
                })
            );
        }
        return getSearchModelRequest;
    }

    setAlertHistory(alertHistory: AlertHistoryModel) {
        this.alertHistory = alertHistory;
    }

    getChart() {
        return this.chart$;
    }

    setChart() {
        this.chart$.next(this.chart);
    }

    getSentimentChart() {
        return this.sentimentChart$;
    }

    setSentimentChart() {
        this.sentimentChart$.next(this.sentimentChart);
    }

    setChartYValueLabel(label: string) {
        if (this.chartOptions) {
            this.chartOptions.yValueLabel = this.localizationService.transform(label);
        }
    }

    setDefaultChartOptions() {
        this.chartOptions = {
            xValueLabel: this.localizationService.transform('MediaSubscriptions.dashboard.quotaOverTime.timeAxis'),
            yValueLabel: this.localizationService.transform('Alerts.surge.dashboard.trend', { title: this.metaData.primarySeriesLabelText })
        };
        this.sentimentChartOptions = {
            xValueLabel: this.localizationService.transform('MediaSubscriptions.dashboard.quotaOverTime.timeAxis'),
            yValueLabel: this.localizationService.transform('Alerts.surge.dashboard.sentiment', { title: this.metaData.primarySeriesLabelText })
        };
    }

    setChartOptions(surgeSourceFacets: DateFacetValueModel[]) {
        const dateGranularity = { timeUnit: 'hour' as TimeUnit, 'count': 1 };
        this.chartOptions.ignoreBullets = true;
        this.chartOptions.minXValue = Number(surgeSourceFacets?.[0]?.from || this.searchCriteria?.neoFacets?.[0]?.range?.start);
        this.chartOptions.maxXValue = Number(moment(surgeSourceFacets?.[surgeSourceFacets.length -1]?.to || this.searchCriteria?.neoFacets?.[0]?.range?.end).tz(this.preferences.timeZone));
        this.chartOptions.notCurved = true;
        this.chartOptions.dateGranularity = dateGranularity;
        this.chartOptions.groupIntervals = [{ timeUnit: 'hour', count: 1 }];
    }

    setChartMinMax(metric?: AlertSurgeMetricEnum, sensitivity?: number) {
        let minYValue = 0;
        if (metric === AlertSurgeMetricEnum.SENTIMENT) {
            minYValue = this.minYValue;
            if (sensitivity && (Math.round(this.avgNegYValue * (sensitivity / 100)) < minYValue)) {
                minYValue = Math.round(this.avgNegYValue * (sensitivity / 100));
            }
        }

        let maxYValue = this.maxYValue;
        if (sensitivity && (Math.round(this.avgYValue * (sensitivity / 100)) > maxYValue)) {
            maxYValue = Math.round(this.avgYValue * (sensitivity / 100));
        }

        this.chartOptions.showLinesInLegend = true;
        if (this.minYValue) {
            this.chartOptions.minYValue = minYValue * 1.05;
        } else {
            this.chartOptions.minYValue = 0;
        }
        this.chartOptions.maxYValue = maxYValue * 1.05;
    }

    getSentimentChartStyles(): ChartStylesModel {
        const chartStyles = new ChartStylesModel();
        chartStyles.colorMap = {
            'POSITIVE': this.defaultChartColors.sentimentColors.positive,
            'NEUTRAL': this.defaultChartColors.sentimentColors.neutral,
            'NOT_AVAILABLE': this.defaultChartColors.sentimentColors.notAvailable,
            'NEGATIVE': this.defaultChartColors.sentimentColors.negative
        };
        chartStyles.chartWidth = 600;
        chartStyles.chartHeight = 350;
        chartStyles.titleFontSize = 18;
        chartStyles.headerMarginBottom = 30;
        chartStyles.spacingUnderDate = true;
        chartStyles.chartDisplaySettings = new ChartDisplaySettingsModel({
            title: true,
            dateRange: true,
            sourceLink: true
        });
        chartStyles.bulletRadius = 5;

        return chartStyles;
    }

    setFacetsAndFilters() {
        this.searchCriteria.pageSize = 0;
        this.searchCriteria.facets = [];
        this.searchCriteria.dateFacets[0].sliceSizeInSecond = 60 * 60;
        // surge chart used havested date
        this.searchCriteria.dateFacets[0].field = DateFacetTypesEnum.indexed;


        this.searchCriteria.neoFacets = [
            {
                // surge chart used havested date
                dateField: NeoFacetDateFieldApiEnum.TIMESTAMP,
                key: this.neoFacetKey,
                aggregateBucketRequests: [
                    {
                        key: AlertSurgeMetricEnum.IMPRESSIONS + '_' + this.neoFacetKey,
                        field: NeoFacetTermFieldApiEnum.AUDIENCE_REACH,
                        function: BucketFunctionApiEnum.INDIVIDUAL_SUM
                    },
                    {
                        key: ReportsNeoFacetKeys.aveFacet + '_' + this.neoFacetKey,
                        field: NeoFacetTermFieldApiEnum.AD_VALUE_USD,
                        function: BucketFunctionApiEnum.INDIVIDUAL_SUM
                    }
                ],
                plainBucketRequests: [
                    {
                        key: AlertSurgeMetricEnum.SENTIMENT + '_' + this.neoFacetKey,
                        field: NeoFacetTermFieldApiEnum.SENTIMENT
                    }
                ],
                range: this.getStartEnd(),
                gap: 3600000
            }
        ];

        this.filterCriteria.query.dateQuery = this.getDateRangeQuery();
        this.searchCriteria.filterQuery = this.filterCriteria.query;
        this.searchCriteria.savedFilterIds = this.filterCriteria.savedFilterIds;
    }

    getStartEnd(): { start: string; end: string } {
        if (this.alertHistory) {
            const date = moment(this.alertHistory?.submittedDate);
            const duration = moment.duration(moment().diff(date));
            const hours = duration.asHours();
            if (hours >= 36) {
                return {
                    start: date.clone().subtract(132, 'hours').toISOString(),
                    end: date.clone().add(36, 'hours').toISOString()
                };
            }
        }
        return {
            start: moment().startOf('hour').subtract(7,'d').toISOString(),
            end: moment().toISOString(),
        };
    }
    async generateChart(metric?: AlertSurgeMetricEnum, sensitivity?: number, selectedSource?: MonitoringBackEndDataSourceModel): Promise<void> {
        this.setDefaultChartOptions();
        this.destroyOldChart();

        if (selectedSource) {
            this.searchCriteria = selectedSource.sourceSearchCriteria.clone();
            this.filterCriteria = selectedSource.filterSearchCriteria.clone();
        } else {
            if (this.filterCriteria) {
                // if  the source is not topic, append agent / theme id below
                if (!this.searchCriteria) {
                    this.searchCriteria = this.filterCriteria.clone();
                }
            } else {
                return;
            }
        }
        this.setFacetsAndFilters();

        const searchCriteria = this.searchCriteria.getApiEntity();
        searchCriteria.query.publicationDate = this.getPublicationDate();
        searchCriteria.query.harvestedDate = this.getPublicationDate();
        if (this.alert.alertIsAgentSearch) {
            searchCriteria.query.agent.include = [this.alert.searchCriteriaId];
        } else if (this.alert.alertIsThemeSearch) {
            searchCriteria.query.theme.include = [this.alert.searchCriteriaId];
        } else if (this.alert.alertIsSubscriptionSearch) {
            searchCriteria.query.subscription.include = [this.alert.searchCriteriaId];
        }
        searchCriteria.requireCombinedQueryResults = true;

        const searchkey: string = JSON.stringify(this.searchCriteria);
        const cachedResponse: MentionsSearchResponseModel = await this.indexedDbCoreCacheService.get(searchkey);

        if (searchCriteria.query.publicationDate && !searchCriteria.query.publicationDate.timeZone) {
            searchCriteria.query.publicationDate.timeZone = this.userTimezonePreference;
        }
        if (searchCriteria.query.harvestedDate && !searchCriteria.query.harvestedDate.timeZone) {
            searchCriteria.query.harvestedDate.timeZone = this.userTimezonePreference;
        }

        if (cachedResponse) {
            this.handleResponse(cachedResponse, metric, sensitivity);
        } else {
            this.requests = this.mentionsApi.searchMentions(searchCriteria).subscribe((response: MentionsSearchResponseModel) => {
                this.requests?.unsubscribe();
                this.requests = null;
                this.searchCriteria.query = new MentionSearchQueryModel(response.combinedQuery);

                this.indexedDbCoreCacheService.set(searchkey, response);

                this.handleResponse(response, metric, sensitivity);
            });
        }
    }

    getDateRangeQuery(): ReportDateRangeModel {
        const returnDateQueryModel = new ReportDateRangeModel();

        if (this.alertHistory) {
            const date = moment(this.alertHistory?.submittedDate);
            const duration = moment.duration(moment().diff(date));
            const hours = duration.asHours();
            if (hours >= 36) {
                const startTime = date.clone().subtract(132, 'hours').toDate();
                const endTime = date.clone().add(36, 'hours').toDate();
                delete returnDateQueryModel.relativeTimezoneDateType;
                returnDateQueryModel.type = DateQueryTypeEnum.ABSOLUTE;
                returnDateQueryModel.absoluteRange = {
                    start: startTime,
                    end: endTime
                };
                return returnDateQueryModel;
            }
        }
        returnDateQueryModel.type =  DateQueryTypeEnum.RELATIVE;
        returnDateQueryModel.relativeEndOffsetH = 0;
        returnDateQueryModel.relativeStartOffsetH = -168;
        delete returnDateQueryModel.relativeTimezoneDateType;

        return returnDateQueryModel;
    }

    getPublicationDate(): SearchCriteriaDateQueryModel {
        const returnDateQueryModel = new SearchCriteriaDateQueryModel();

        if (this.alertHistory) {
            const date = moment(this.alertHistory?.submittedDate);
            const duration = moment.duration(moment().diff(date));
            const hours = duration.asHours();
            if (hours >= 36) {
                const startTime = date.clone().subtract(132, 'hours').toDate();
                const endTime = date.clone().add(36, 'hours').toDate();

                delete returnDateQueryModel.relativeTimezoneDateType;
                returnDateQueryModel.dateType = DateQueryTypeEnum.ABSOLUTE;
                returnDateQueryModel.dateRange = {
                    start: startTime.getTime(),
                    end: endTime.getTime()
                };
                return returnDateQueryModel;
            }
        }
        returnDateQueryModel.dateType =  DateQueryTypeEnum.RELATIVE;
        returnDateQueryModel.relativeEndOffsetH = 0;
        returnDateQueryModel.relativeStartOffsetH = -168;
        return returnDateQueryModel;
    }

    handleResponse(response: MentionsSearchResponseModel, metric?: AlertSurgeMetricEnum, sensitivity?: number) {
        const surgeSourceFacets = <DateFacetValueModel[]>response?.dateFacetResults[0]?.values || [];
        const surgeSourceNeoFacets = <BucketResponseModel[]>response?.neoFacetResults[0]?.buckets || [];

        if (metric && sensitivity) {
            let surgeSourceDataPoints: DataPointModel[];

            if (metric === AlertSurgeMetricEnum.MENTION_VOLUME) {
                surgeSourceDataPoints = this.convertFacetsToDataPoints(surgeSourceFacets);
            } else if (metric === AlertSurgeMetricEnum.SENTIMENT) {
                surgeSourceDataPoints = this.convertSentimentsFacetsToDataPoints(surgeSourceNeoFacets, false);
            } else {
                surgeSourceDataPoints = this.convertNeoFacetsToDataPoints(AlertSurgeMetricEnum.IMPRESSIONS, surgeSourceNeoFacets);
            }

            if (metric !== AlertSurgeMetricEnum.SENTIMENT) {
                this.setChartOptions(surgeSourceFacets);
            }
            this.chartOptions.addTooltipValue = true;

            this.setChartMinMax(metric, sensitivity);

            if (metric === AlertSurgeMetricEnum.SENTIMENT) {
                this.chartOptions.horizontalLines = [
                    {
                        position: Math.round(this.avgNegYValue * (sensitivity / 100)),
                        color: chartColors.agprNegative,
                        fill: chartColors.agprNegative,
                        strokeWidth: 2,
                        tooltip: this.localizationService.transform(`Alerts.dialogs.surge.sensitivity.threshold.${SearchCriteriaQuerySentimentsEnum.negative}`),
                        name: this.localizationService.transform(`Alerts.dialogs.surge.sensitivity.threshold.${SearchCriteriaQuerySentimentsEnum.negative}`),
                    },
                    {
                        position: Math.round(this.avgYValue * (sensitivity / 100)), // sensitivity is a percentage
                        color: chartColors.agprPositive,
                        fill: chartColors.agprPositive,
                        strokeWidth: 2,
                        tooltip: this.localizationService.transform(`Alerts.dialogs.surge.sensitivity.threshold.${SearchCriteriaQuerySentimentsEnum.positive}`),
                        name: this.localizationService.transform(`Alerts.dialogs.surge.sensitivity.threshold.${SearchCriteriaQuerySentimentsEnum.positive}`),
                    }
                ];
            } else {
                this.chartOptions.horizontalLines = [
                    {
                        position: Math.round(this.avgYValue * (sensitivity / 100)), // sensitivity is a percentage
                        color: this.defaultChartColors.otherColors.engagements,
                        fill: this.defaultChartColors.otherColors.engagements,
                        strokeWidth: 2,
                        tooltip: this.localizationService.transform('Alerts.dialogs.surge.sensitivity.threshold'),
                        name: this.localizationService.transform('Alerts.dialogs.surge.sensitivity.threshold'),
                    },
                    {
                        position: Math.round(this.avgYValue),
                        color: this.defaultChartColors.otherColors.impressions,
                        fill: this.defaultChartColors.otherColors.impressions,
                        strokeWidth: 2,
                        tooltip: this.localizationService.transform(`Alerts.dialogs.surge.sensitivity.average.${metric}`),
                        name: this.localizationService.transform(`Alerts.dialogs.surge.sensitivity.average.${metric}`),
                    }
                ];
            }

            const chartSeries: LineSeriesModel[] = [
                {
                    label: this.localizationService.transform(`Alerts.dialogs.surge.sensitivity.trend.${metric}`),
                    dataProvider: of(surgeSourceDataPoints),
                    color: this.defaultChartColors.generalColors.color1,
                    projectionStrokeSize: 2,
                    projectionStrokeDashArray: '8,3'
                }
            ];

            if (metric === AlertSurgeMetricEnum.SENTIMENT) {
                this.chartOptions.seriesDefinitions = [];
                this.chartOptions.seriesDefinitions.push({
                    key: SearchCriteriaQuerySentimentsEnum.negative,
                    label: this.localizationService.transform('Reports.sentiment.negative')
                });
                this.chartOptions.seriesDefinitions.push({
                    key: SearchCriteriaQuerySentimentsEnum.positive,
                    label: this.localizationService.transform('Reports.sentiment.positive')
                });
                this.chartOptions.styles = this.getSentimentChartStyles();
                this.chart = new PositiveNegativeRiverChartModel(chartSeries, this.chartOptions, this.metaData);
            } else {
                this.chartOptions.customLegendItems = [];
                this.chartOptions.customLegendItems.push({
                    fill: this.defaultChartColors.generalColors.color1,
                    color: this.defaultChartColors.generalColors.color1,
                    label: this.localizationService.transform(`Alerts.dialogs.surge.sensitivity.trend.${metric}`),
                    name: this.localizationService.transform(`Alerts.dialogs.surge.sensitivity.trend.${metric}`)
                });
                this.chartOptions.customLegendItems.push({
                    fill: this.defaultChartColors.otherColors.impressions,
                    color: this.defaultChartColors.otherColors.impressions,
                    label: this.localizationService.transform(`Alerts.dialogs.surge.sensitivity.average.${metric}`),
                    name: this.localizationService.transform(`Alerts.dialogs.surge.sensitivity.average.${metric}`),
                });
                this.chartOptions.customLegendItems.push({
                    fill: this.defaultChartColors.otherColors.engagements,
                    color: this.defaultChartColors.otherColors.engagements,
                    label: this.localizationService.transform('Alerts.dialogs.surge.sensitivity.threshold'),
                    name: this.localizationService.transform('Alerts.dialogs.surge.sensitivity.threshold'),
                });

                this.chart = new DateAndValueXYChart(chartSeries, this.chartOptions, this.metaData);
            }
            this.setChart();

        } else {
            const mentionSourceDataPoints = this.convertFacetsToDataPoints(surgeSourceFacets);
            const impressionsSourceDataPoints = this.convertNeoFacetsToDataPoints(AlertSurgeMetricEnum.IMPRESSIONS, surgeSourceNeoFacets);
            const aveSourceDataPoints = this.convertNeoFacetsToDataPoints(ReportsNeoFacetKeys.aveFacet, surgeSourceNeoFacets);

            // Handle MultiSeriesDateAndValueXYChart
            this.setChartOptions(surgeSourceFacets);
            this.setChartMinMax();
            this.chartOptions.addTooltipValue = true;
            this.chartOptions.isMultiAxis = true;
            this.chartOptions.centerLegend = true;
            this.chartOptions.doReadyInInit = true;
            this.chartOptions.styles = new ChartStylesModel({
                colorMap: {
                    'seriesColor0': this.defaultChartColors.otherColors.volume,
                    'seriesColor1': this.defaultChartColors.otherColors.impressions,
                    'seriesColor2': this.defaultChartColors.otherColors.engagements,
                },
                chartDisplaySettings: {
                    title: true,
                    dateRange: true,
                    sourceLink: true
                },
                chartWidth: 600,
                chartHeight: 350,
                spacingUnderDate: true
            });

            const dateSeries = new ReportSeriesModel(null, ReportBlockTypeEnum.COVERAGE_OVER_TIME);

            const volumneSeries = dateSeries.clone();
            volumneSeries.seriesUniqueUuid = uuid.v4();
            volumneSeries.label = this.localizationService.transform(`Alerts.surge.metrics.${AlertSurgeMetricEnum.MENTION_VOLUME}`);
            volumneSeries.dataProvider = new BehaviorSubject<DataPointModel[]>(mentionSourceDataPoints);
            volumneSeries.color = this.defaultChartColors.otherColors.volume;
            volumneSeries.metric = ChartMetrics.volumeOfMentions;
            volumneSeries.dateRange = this.getDateRangeQuery();
            volumneSeries.dateRange.dateType = DateQueryDateTypeEnum.HARVESTED;
            volumneSeries.mentions = this.getReportSeriesMentionsModel(volumneSeries);

            const impressionSeries = dateSeries.clone();
            impressionSeries.seriesUniqueUuid = uuid.v4();
            impressionSeries.label = this.localizationService.transform(`Alerts.surge.metrics.${AlertSurgeMetricEnum.IMPRESSIONS}`);
            impressionSeries.dataProvider = new BehaviorSubject<DataPointModel[]>(impressionsSourceDataPoints);
            impressionSeries.color = this.defaultChartColors.otherColors.impressions;
            impressionSeries.metric = ChartMetrics.audienceReach;
            impressionSeries.dateRange = this.getDateRangeQuery();
            impressionSeries.dateRange.dateType = DateQueryDateTypeEnum.HARVESTED;
            impressionSeries.mentions = this.getReportSeriesMentionsModel(impressionSeries);

            const aveSeries = dateSeries.clone();
            aveSeries.seriesUniqueUuid = uuid.v4();
            aveSeries.label = this.localizationService.transform(`Alerts.surge.dashboard.${ChartMetricsEnum.ave}`);
            aveSeries.dataProvider = new BehaviorSubject<DataPointModel[]>(aveSourceDataPoints);
            aveSeries.color = this.defaultChartColors.otherColors.engagements;
            aveSeries.metric = ChartMetricsEnum.ave;
            aveSeries.dateRange = this.getDateRangeQuery();
            aveSeries.dateRange.dateType = DateQueryDateTypeEnum.HARVESTED;
            aveSeries.mentions = this.getReportSeriesMentionsModel(aveSeries);

            const chartSeries: ReportSeriesModel[] = [volumneSeries, impressionSeries, aveSeries];


            const sentimentsTrendSeries = new ReportSeriesModel(null, ReportBlockTypeEnum.SENTIMENT_TREND);
            const sentimentsSeries = sentimentsTrendSeries.clone();
            sentimentsSeries.label = this.localizationService.transform(`Alerts.surge.metrics.${AlertSurgeMetricEnum.SENTIMENT}`);
            sentimentsSeries.color = this.defaultChartColors.generalColors.color1;
            sentimentsSeries.metric = ChartMetrics.positiveSentimentPercentage;
            sentimentsSeries.dateRange = this.getDateRangeQuery();
            sentimentsSeries.dateRange.dateType = DateQueryDateTypeEnum.HARVESTED;
            sentimentsSeries.mentions = this.getReportSeriesMentionsModel(sentimentsSeries);
            const sentimentSeries: ReportSeriesModel[] = [sentimentsSeries];

            if (this.alert.alertIsAgentSearch) {
                volumneSeries.styles.socialTopicThemeIds = this.socialTopicThemeIds;
                impressionSeries.styles.socialTopicThemeIds = this.socialTopicThemeIds;
                aveSeries.styles.socialTopicThemeIds = this.socialTopicThemeIds;
                sentimentsSeries.styles.socialTopicThemeIds = this.socialTopicThemeIds;
            }
            const sentimentSourceDataPoints = this.convertSentimentsFacetsToDataPoints(surgeSourceNeoFacets, true, sentimentsSeries);
            sentimentsSeries.dataProvider = new BehaviorSubject<DataPointModel[]>(sentimentSourceDataPoints);

            this.chartOptions.seriesDefinitions = [];
            this.chartOptions.seriesDefinitions.push({
                key: ChartMetricsEnum.volumeOfMentions,
                label: this.localizationService.transform('Reports.analysis.type.mentions')
            });
            this.chartOptions.seriesDefinitions.push({
                key: ChartMetricsEnum.ave,
                label: this.localizationService.transform('Reports.analysis.type.ave') + `(${this.preferences.currency})`
            });
            this.chartOptions.seriesDefinitions.push({
                key: ChartMetricsEnum.IMPRESSIONS,
                label: this.localizationService.transform('Reports.analysis.type.audienceReach')
            });

            this.sentimentChartOptions.exportLabelDefinitions = new Map<string, string>([
                ['POSITIVE', this.localizationService.transform('Reports.sentiment.positive.count')],
                ['NEGATIVE', this.localizationService.transform('Reports.sentiment.negative.count')],
                ['NEUTRAL', this.localizationService.transform('Reports.sentiment.neutral.count')],
                ['NOT_AVAILABLE', this.localizationService.transform('Reports.sentiment.notavailable.count')],
                ['POSITIVEPercent', this.localizationService.transform('Reports.sentiment.positive.percent')],
                ['NEGATIVEPercent', this.localizationService.transform('Reports.sentiment.negative.percent')],
                ['NEUTRALPercent', this.localizationService.transform('Reports.sentiment.neutral.percent')],
                ['NOT_AVAILABLEPercent', this.localizationService.transform('Reports.sentiment.notavailable.percent')]
            ]);
            this.sentimentChartOptions.seriesDefinitions = [];
            this.sentimentChartOptions.seriesDefinitions.push({
                key: SearchCriteriaQuerySentimentsEnum.negative,
                label: this.localizationService.transform('Reports.sentiment.negative')
            });
            this.sentimentChartOptions.seriesDefinitions.push({
                key: SearchCriteriaQuerySentimentsEnum.notAvailable,
                label: this.localizationService.transform('Reports.sentiment.notAvailable')
            });
            this.sentimentChartOptions.seriesDefinitions.push({
                key: SearchCriteriaQuerySentimentsEnum.neutral,
                label: this.localizationService.transform('Reports.sentiment.neutral')
            });
            this.sentimentChartOptions.seriesDefinitions.push({
                key: SearchCriteriaQuerySentimentsEnum.positive,
                label: this.localizationService.transform('Reports.sentiment.positive')
            });
            this.sentimentChartOptions.styles = this.getSentimentChartStyles();
            this.sentimentChartOptions.addTooltipValue = true;
            this.sentimentChartOptions.ignoreBullets = true;
            this.sentimentChartOptions.minXValue = Number(surgeSourceFacets?.[0]?.from || this.searchCriteria?.neoFacets?.[0]?.range?.start);
            this.sentimentChartOptions.maxXValue = Number(moment(surgeSourceFacets?.[surgeSourceFacets.length -1]?.to || this.searchCriteria?.neoFacets?.[0]?.range?.end).tz(this.preferences.timeZone));
            this.sentimentChartOptions.notCurved = true;
            this.sentimentChartOptions.dateGranularity = { timeUnit: 'hour' as TimeUnit, 'count': 1 };
            this.sentimentChartOptions.groupIntervals = [{ timeUnit: 'hour', count: 1 }];

            if (this.alertHistory) {
                this.chartOptions.verticalLines = [
                    {
                        position: moment(moment(this.alertHistory.submittedDate).tz(this.preferences.timeZone).format('YYYY-MM-DD HH:mm:ss')).valueOf(),
                        color: chartColors.agprLightGreen,
                        strokeWidth: 2,
                        tooltip: this.localizationService.transform('Alerts.surge.dashboard.alertSent')
                    }
                ];
                this.sentimentChartOptions.verticalLines = [
                    {
                        position: moment(moment(this.alertHistory.submittedDate).tz(this.preferences.timeZone).format('YYYY-MM-DD HH:mm:ss')).valueOf(),
                        color: chartColors.agprLightGreen,
                        strokeWidth: 2,
                        tooltip: this.localizationService.transform('Alerts.surge.dashboard.alertSent')
                    }
                ];

                const dateRange = this.getDateRangeQuery();
                this.metaData.dateRangeLabelText = dateRange.type === DateQueryTypeEnum.RELATIVE ?
                    this.localizationService.transform('lastNDays', { 'days': 7 }) + ' (' +
                        moment().add(dateRange.relativeStartOffsetH, 'hours').tz(this.preferences.timeZone).format('YYYY/MM/DD') + ' - ' +
                        moment().add(dateRange.relativeEndOffsetH, 'hours').tz(this.preferences.timeZone).format('YYYY/MM/DD') + ')'
                    : moment(dateRange.absoluteRange.start).tz(this.preferences.timeZone).format('YYYY/MM/DD') + ' - ' +
                        moment(dateRange.absoluteRange.end).tz(this.preferences.timeZone).format('YYYY/MM/DD');
            }
            const dateMetaData = cloneDeep(this.metaData);
            dateMetaData.type = ReportBlockTypeEnum.COVERAGE_OVER_TIME;
            dateMetaData.titleText = this.localizationService.transform('Alerts.surge.dashboard.trend',{ title: dateMetaData.primarySeriesLabelText });
            const sentimentMetaData = cloneDeep(this.metaData);
            sentimentMetaData.type = ReportBlockTypeEnum.SENTIMENT_TREND;
            sentimentMetaData.titleText = this.localizationService.transform('Alerts.surge.dashboard.sentiment',{ title: sentimentMetaData.primarySeriesLabelText });
            this.chart = new MultiSeriesDateAndValueXYChart(chartSeries, this.chartOptions, dateMetaData);
            this.sentimentChart = new StackedRiverChartModel(sentimentSeries, this.sentimentChartOptions, sentimentMetaData);

            this.setChart();
            this.setSentimentChart();
        }
    }

    getReportSeriesMentionsModel(series: ReportSeriesModel): ReportSeriesMentionsModel {

        const seriesMentions: ReportSeriesMentionsModel = {};

        seriesMentions.searchCriteriaId = this.alert.searchCriteriaId;

        if (this.searchCriteria?.query?.subscription?.include?.length) {
            seriesMentions.mediaSubscriptionId = this.searchCriteria.query.subscription.include[0];
            series.type = SeriesTypeEnum.subscription;
        } else if (this.searchCriteria?.query?.theme?.include?.length) {
            seriesMentions.themeId = this.searchCriteria.query.theme.include[0];
            series.type = SeriesTypeEnum.theme;
        } else if (this.searchCriteria?.query?.agent?.include?.length) {
            seriesMentions.socialTopicId = this.searchCriteria.query.agent.include[0];
            series.type = SeriesTypeEnum.socialTopic;
        } else {
            seriesMentions.topicId = this.alert.searchCriteriaId;
            series.type = SeriesTypeEnum.topic;
        }

        seriesMentions.combinedQuery = this.searchCriteria.query;

        return seriesMentions;
    }

    /**
     * Converts facets of timeslots into chartable data points
     */
    convertFacetsToDataPoints(timeslots: DateFacetValueModel[]): DataPointModel[] {
        this.maxYValue = 0;
        this.avgYValue = 0;

        const data = [];
        let total = 0;
        for (let i = 0; i < timeslots.length; i++) {
            const point = timeslots[i];
            total += point.count;
            if (this.maxYValue < point.count) {
                this.maxYValue = point.count;
            }
            data.push({
                xValue: moment(moment(point.to).tz(this.preferences.timeZone).format('YYYY-MM-DD HH:mm:ss')).valueOf(),
                yValue: point.count,
                rawDateFacetFrom: point.from,
                rawDateFacetTo: point.to,
            });
        }

        this.avgYValue = total / timeslots.length;
        return data;
    }

    /**
     * Converts neofacets of timeslots into chartable data points
     */
    convertNeoFacetsToDataPoints(metric: AlertSurgeMetricEnum | ReportsNeoFacetKeys, timeslots: BucketResponseModel[]): DataPointModel[] {
        this.maxYValue = 0;
        this.avgYValue = 0;

        const data = [];
        let total = 0;
        for (let i = 0; i < timeslots.length; i++) {
            const point = timeslots[i];
            const count = timeslots[i].buckets?.[metric + '_' + this.neoFacetKey]?.[0]?.count || 0;
            total += count;
            if (this.maxYValue < count) {
                this.maxYValue = count;
            }
            data.push({
                xValue: moment(moment(point.intervalValue.end).tz(this.preferences.timeZone).format('YYYY-MM-DD HH:mm:ss')).valueOf(),
                yValue: count,
                rawDateFacetFrom: point.intervalValue.start,
                rawDateFacetTo: point.intervalValue.end,
            });
        }

        this.avgYValue = total / timeslots.length;
        return data;
    }

    /**
     * Converts a series of timeslots for subscription content into chartable data points
     */
    convertSentimentsFacetsToDataPoints(timeslots: BucketResponseModel[], stacked: boolean, series?: ReportSeriesModel): DataPointModel[] {
        if (!stacked) {
            this.maxYValue = 0;
            this.minYValue = 0;
            this.avgYValue = 0;
            this.avgNegYValue = 0;
        }

        const data = [];
        let allCounts = 0;
        let total = 0;
        let totalNegative = 0;

        let positiveSum = 0;
        let negativeSum = 0;
        let neutralSum = 0;

        let positivePercentage = 0;
        let negativePercentage = 0;
        let neutralPercentage = 0;

        for (let i = 0; i < timeslots.length; i++) {
            positiveSum = 0;
            negativeSum = 0;
            neutralSum = 0;
            allCounts = 0;
            positivePercentage = 0;
            negativePercentage = 0;
            neutralPercentage = 0;

            const bucket = timeslots[i];
            const sentiments = timeslots[i].buckets?.[AlertSurgeMetricEnum.SENTIMENT + '_' + this.neoFacetKey] as BucketResponseModel[] || [];

            for (const sentiment of sentiments) {
                if (+sentiment.termValue >= 0.3) {
                    positiveSum += sentiment.count;
                } else if (+sentiment.termValue >= -1 && +sentiment.termValue <= -0.3) {
                    if (stacked) {
                        negativeSum += sentiment.count;
                    } else {
                        negativeSum -= sentiment.count;
                    }
                } else if (+sentiment.termValue < 0.3 && +sentiment.termValue > -0.3) {
                    neutralSum += sentiment.count;
                }
                allCounts += sentiment.count;
            }
            if (allCounts) {
                positivePercentage = (positiveSum / allCounts) * 100;
                negativePercentage = (negativeSum / allCounts) * 100;
                neutralPercentage = (neutralSum / allCounts) * 100;
            }

            total += positivePercentage;
            totalNegative += negativePercentage;
            if (!stacked) {
                if (this.maxYValue < positivePercentage) {
                    this.maxYValue = positivePercentage;
                }
                if (this.minYValue > negativePercentage) {
                    this.minYValue = negativePercentage;
                }
            }
            const dataPoint: DataPointModel = {
                xValue: moment(moment(bucket.intervalValue.end).tz(this.preferences.timeZone).format('YYYY-MM-DD HH:mm:ss')).valueOf(),
                rawDateFacetFrom: bucket.intervalValue.start,
                rawDateFacetTo: bucket.intervalValue.end,
                [`yValue_${SearchCriteriaQuerySentimentsEnum.positive}`]: positiveSum,
                [`yValue_${SearchCriteriaQuerySentimentsEnum.neutral}`]: neutralSum,
                [`yValue_${SearchCriteriaQuerySentimentsEnum.notAvailable}`]: bucket.count - positiveSum - negativeSum - neutralSum,
                [`yValue_${SearchCriteriaQuerySentimentsEnum.negative}`]: negativeSum,
                [`yPercent_${SearchCriteriaQuerySentimentsEnum.positive}`]: positivePercentage,
                [`yPercent_${SearchCriteriaQuerySentimentsEnum.negative}`]: negativePercentage,
            };

            if (series) {
                Object.values(SearchCriteriaQuerySentimentsEnum).forEach(sentiment => {
                    const criteria = new MentionSearchCriteriaModel();
                    criteria.query.sentiments = [new SentimentModel(sentiment)];
                    criteria.query.dateQuery = DateQueryModel.getAbsoluteQuery();
                    criteria.query.dateQuery.absoluteRange = {
                        start: new Date(bucket.intervalValue.start),
                        end: new Date(bucket.intervalValue.end)
                    };
                    dataPoint['viewMentionUrl_' + sentiment] = series.getMentionSeriesUrl(criteria);
                });
            }

            data.push(dataPoint);
        }

        if (!stacked) {
            this.avgYValue = total / timeslots.length;
            this.avgNegYValue = totalNegative / timeslots.length;
        }

        return data;
    }

    destroyOldChart(): void {
        this.chart = null;
        this.sentimentChart = null;

        if (this.requests) {
            this.requests.unsubscribe();
            this.requests = null;
        }
        this.setChart();
        this.setSentimentChart();
    }

    ngOnDestroy() {
        this.destroyOldChart();
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
